import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Fetches a PDF from a URL and performs basic validation.
 * Ensures the response is OK, has a non-zero size, is a PDF MIME type,
 * and contains the standard PDF header.
 *
 * @param url URL to fetch the PDF from
 * @throws Error if the file cannot be fetched or fails validation
 * @returns Blob of the validated PDF file
 */
export async function validateDownloadedPdf(url: string): Promise<Blob> {
  const response = await fetch(url)

  if (!response.ok) {
    throw new Error('File not found or inaccessible.')
  }

  const fileBlob = await response.blob()

  if (!fileBlob || fileBlob.size === 0) {
    throw new Error('File is empty or corrupted.')
  }

  const contentType = fileBlob.type || response.headers.get('content-type') || ''
  if (!contentType.includes('pdf') && !contentType.includes('application/pdf')) {
    throw new Error('File is not a valid PDF document.')
  }

  const arrayBuffer = await fileBlob.arrayBuffer()
  const uint8Array = new Uint8Array(arrayBuffer)
  const pdfHeader = new TextDecoder().decode(uint8Array.slice(0, 4))

  if (pdfHeader !== '%PDF') {
    throw new Error('File content is not a valid PDF.')
  }

  return fileBlob
}
